/**
 * Record-detail shell chrome — hide primary + secondary rails so the record
 * owns the canvas. Breadcrumb keeps way-back (P1); SiteHeader omits SidebarTrigger.
 *
 * Pattern: `docs/record-detail-chrome-pattern.md`
 * Job: `docs/jobs/record-detail.md`
 */

import { isLearningActivitiesCourseDetailPath } from "@/lib/learning-activities-nav"

function normalizePathname(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname
}

/**
 * Assessment record page — `/…/assessments/:id` only.
 * Nested compose / preview / add-questions routes stay focus or hub chrome.
 */
export function isAssessmentDetailPath(pathname: string): boolean {
  return /\/assessments\/[^/]+$/.test(normalizePathname(pathname))
}

/** Grading submission record — `/…/submissions/:id`. */
export function isSubmissionDetailPath(pathname: string): boolean {
  return /\/submissions\/[^/]+$/.test(normalizePathname(pathname))
}

/** Answer-key adjustment — `/…/assessments/:id/adjust-key`. */
export function isAssessmentAdjustKeyPath(pathname: string): boolean {
  return /\/assessments\/[^/]+\/adjust-key$/.test(normalizePathname(pathname))
}

/** Routes that use record-detail chrome (no app nav rail). Extend per entity. */
export function isRecordDetailChromePath(pathname: string): boolean {
  const normalized = normalizePathname(pathname)
  return (
    isLearningActivitiesCourseDetailPath(normalized) ||
    isSubmissionDetailPath(normalized) ||
    isAssessmentAdjustKeyPath(normalized)
  )
}

/**
 * Record routes whose compact utility bar uses **Back mode** (icon-only parent)
 * instead of the default breadcrumb trail slot. Assessment detail uses the
 * standard hub utility bar (product switch + sidebar); submissions and adjust-key
 * keep back mode — see `docs/exxat-ds/handoff/assessment-details-chrome.md`.
 */
export function isRecordDetailUtilityBackPath(pathname: string): boolean {
  const normalized = normalizePathname(pathname)
  return (
    isLearningActivitiesCourseDetailPath(normalized) ||
    isSubmissionDetailPath(normalized) ||
    isAssessmentAdjustKeyPath(normalized)
  )
}
